import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Users, Award, Shield, ChevronRight, CalendarClock, MessageSquareWarning, Clock, CheckCircle2, Gavel, ShieldAlert, DollarSign, Library, FolderArchive, CalendarX2, HeartHandshake, Drama, Clapperboard } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SummaryCard from "@/components/SummaryCard";
import BroadcastDialog from "@/components/BroadcastDialog";
import UpcomingEvents from "@/components/UpcomingEvents";
import AttendanceTodayCard from "@/components/AttendanceTodayCard";
import { formatDateOnly } from "@/lib/date";
import PageHeader, { schoolYear } from "@/components/PageHeader";
import DashboardNotificationsCard from "@/components/notifications/DashboardNotificationsCard";
import api from "@/lib/api";
import { cachedGet } from "@/lib/cachedGet";
import { useAuth } from "@/context/AuthContext";
import { useFeatureFlags } from "@/lib/featureFlags";
import TheatreSetupCard from "@/components/theatre/TheatreSetupCard";
import { GivenUpShifts, RepostedOpenShifts } from "@/components/VolunteerSwaps";
import { toast } from "sonner";
import { shortenNos } from "@/lib/caseNo";

function fmtDeadline(iso) {
  if (!iso) return null;
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return null;
  return dt.toLocaleString(undefined, { timeZone: "America/Chicago", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// Countdown label + urgency once within 24h of the deadline. null = not near yet / already passed.
function countdown(iso, nowMs) {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  if (isNaN(end)) return null;
  const ms = end - nowMs;
  if (ms <= 0) return null;
  const hours = ms / 3600000;
  if (hours > 24) return null;
  const label = ms < 3600000 ? `Closes in ${Math.max(1, Math.round(ms / 60000))}m` : `Closes in ${Math.round(hours)}h`;
  return { label, urgent: hours <= 3 };
}

function CoachDeletionRequests() {
  const [items, setItems] = useState([]);
  const load = React.useCallback(() => api.get("/coach/deletion-requests").then(({ data }) => setItems(data.items || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const act = async (rid, action) => {
    try {
      await api.post(`/coach/deletion-requests/${rid}/${action}`);
      toast.success(action === "request-deletion" ? "Forwarded to the System Administrator" : "Request declined");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };
  if (items.length === 0) return null;
  return (
    <Card className="mt-4 p-5 border-amber-200 bg-amber-50/40 shadow-sm rounded-xl" data-testid="coach-deletion-requests-card">
      <div className="mb-3 flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 text-amber-600" />
        <p className="text-xs font-bold uppercase tracking-wider text-amber-700">Data deletion requests</p>
      </div>
      <p className="mb-3 text-xs text-slate-500">No District/Campus Administrator is assigned to your team, so these came to you. Forward to the System Administrator to complete the deletion.</p>
      <div className="space-y-2">
        {items.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-100 bg-white p-3" data-testid={`coach-deletion-request-${r.id}`}>
            <div>
              <p className="text-sm font-semibold text-slate-800">{r.requester_name} <span className="font-normal text-slate-400">({r.requester_role === "parent" ? "Parent" : "Team Member"})</span></p>
              <p className="text-xs text-slate-500">{r.requester_email}{r.reason ? ` · "${r.reason}"` : ""}</p>
            </div>
            {r.status === "coach_requested" ? (
              <span className="rounded-full bg-sky-100 px-2.5 py-1 text-xs font-semibold text-sky-700" data-testid={`coach-deletion-sent-${r.id}`}>Sent to System Admin</span>
            ) : (
              <div className="flex gap-2">
                <Button size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => act(r.id, "request-deletion")} data-testid={`coach-deletion-forward-${r.id}`}>Request deletion</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(r.id, "deny")} data-testid={`coach-deletion-deny-${r.id}`}>Decline</Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function CoachStaffRequests() {
  const [items, setItems] = useState([]);
  const load = React.useCallback(() => api.get("/coach/staff-requests").then(({ data }) => setItems(data.items || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const act = async (uid, action) => {
    try {
      await api.post(`/coach/staff-requests/${uid}/${action}`);
      toast.success(action === "approve" ? "Coach approved" : "Request rejected");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };
  if (items.length === 0) return null;
  return (
    <Card className="mt-4 p-5 border-sky-200 bg-sky-50/40 shadow-sm rounded-xl" data-testid="coach-join-requests-card">
      <div className="mb-3 flex items-center gap-2">
        <Users className="h-4 w-4 text-sky-600" />
        <p className="text-xs font-bold uppercase tracking-wider text-sky-700">Coach join requests</p>
      </div>
      <div className="space-y-2">
        {items.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-sky-100 bg-white p-3" data-testid={`coach-join-request-${u.id}`}>
            <div>
              <p className="text-sm font-semibold text-slate-800">{u.first_name} {u.last_name}</p>
              <p className="text-xs text-slate-500">{u.email} · wants to join your team as a coach</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => act(u.id, "approve")} data-testid={`coach-join-approve-${u.id}`}>Approve</Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(u.id, "reject")} data-testid={`coach-join-reject-${u.id}`}>Reject</Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// Stage-manager reports awaiting director approval; re-checks every minute and when the tab regains focus.
function useShowNotesPending(on) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!on) return undefined;
    const load = () => api.get("/show-notes/pending-count").then(({ data }) => setCount(data.count || 0)).catch(() => {});
    const onVis = () => document.visibilityState === "visible" && load();
    load();
    const t = setInterval(load, 60000);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", load);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", load); };
  }, [on]);
  return count;
}

function CoachConflictsCard() {
  const [count, setCount] = useState(null);
  useEffect(() => { api.get("/conflicts/pending-count").then(({ data }) => setCount(data.count || 0)).catch(() => {}); }, []);
  if (!count) return null;
  return (
    <Link to="/coach/schedule?tab=conflicts" className="mt-4 block">
      <Card data-testid="coach-conflicts-card" className="p-5 border-slate-200 shadow-sm rounded-xl bg-white hover:shadow-md transition-transform hover:-translate-y-0.5 cursor-pointer animate-fade-in-up">
        <div className="mb-4 flex items-center gap-2">
          <CalendarX2 className="h-4 w-4 text-slate-500" />
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Conflicts</p>
          <ChevronRight className="ml-auto h-4 w-4 text-slate-400" />
        </div>
        <div className="flex flex-col items-center px-3 text-center">
          <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${count ? "bg-orange-50 text-orange-600" : "bg-slate-50 text-slate-400"}`}><CalendarX2 className="h-5 w-5" /></span>
          <p className="mt-3 text-3xl font-extrabold text-slate-900 font-heading tabular-nums" data-testid="coach-conflicts-pending">{count ?? "—"}</p>
          <p className="mt-1 text-xs font-semibold text-slate-500">Conflicts awaiting review</p>
        </div>
      </Card>
    </Link>
  );
}

function CoachVolunteerCard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/volunteers/summary").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s?.open_spots) return null;
  return (
    <Link to="/coach/schedule?tab=volunteers" className="mt-4 block">
      <Card data-testid="coach-volunteer-card" className="p-5 border-teal-200 bg-teal-50/40 shadow-sm rounded-xl hover:shadow-md transition-transform hover:-translate-y-0.5 cursor-pointer animate-fade-in-up">
        <div className="flex items-center gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-100 text-teal-700"><HeartHandshake className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold uppercase tracking-wider text-teal-700">Volunteer Sign-Up</p>
            <p className="mt-0.5 text-sm font-semibold text-slate-800" data-testid="coach-volunteer-open">
              {s.open_spots} open spot{s.open_spots === 1 ? "" : "s"} across {s.events_with_openings} upcoming date{s.events_with_openings === 1 ? "" : "s"}
            </p>
            {s.next && <p className="text-xs text-slate-500" data-testid="coach-volunteer-next">Next: {s.next.title} · {formatDateOnly(s.next.event_date)} ({s.next.open} open)</p>}
          </div>
          <ChevronRight className="h-4 w-4 text-slate-400" />
        </div>
      </Card>
    </Link>
  );
}

function CoachGivenUpCard() {
  const [data, setData] = useState({});
  const { viewOnly } = useAuth();
  const load = React.useCallback(() => api.get("/volunteers/swaps").then(({ data: d }) => setData(d)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const items = data.given_up || [];
  const reposted = data.open_board || [];
  if ((!items.length && !reposted.length) || viewOnly) return null;
  return (
    <Card className="mt-4 border-teal-200 bg-teal-50/40 p-5 shadow-sm rounded-xl animate-fade-in-up" data-testid="coach-given-up-card">
      <div className="mb-3 flex items-center gap-2">
        <HeartHandshake className="h-4 w-4 text-teal-700" />
        <p className="text-xs font-bold uppercase tracking-wider text-teal-700">Shifts given up</p>
        {items.length > 0 && <span className="rounded-full bg-teal-600 px-2 py-0.5 text-[11px] font-bold text-white" data-testid="coach-given-up-count">{items.length}</span>}
      </div>
      <p className="mb-3 text-xs text-slate-500">Only you can see who gave these up. Reposting takes them off the shift and lets the first teammate who claims it take the spot.</p>
      <div className="space-y-3">
        {items.length > 0 && <GivenUpShifts items={items} onDone={load} />}
        <RepostedOpenShifts items={reposted} />
      </div>
    </Card>
  );
}

function PendingRemindersBanner() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/coach/pending-reminders").then(({ data }) => setItems(data.items || [])).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <Card className="mb-4 border-amber-300 bg-amber-50 p-4 shadow-sm animate-fade-in-up" data-testid="pending-reminders-banner">
      <div className="mb-2 flex items-center gap-2">
        <MessageSquareWarning className="h-5 w-5 text-amber-600" />
        <h3 className="font-heading text-base font-semibold text-amber-800">
          {items.length} request{items.length === 1 ? "" : "s"} need{items.length === 1 ? "s" : ""} your attention
        </h3>
      </div>
      <div className="space-y-2">
        {items.map((r) => (
          <Link key={`${r.type}-${r.id}`} to={r.link}
            className="flex items-start gap-2 rounded-lg border border-amber-200 bg-white p-3 transition-colors hover:bg-amber-100/50"
            data-testid={`pending-reminder-${r.type}-${r.id}`}>
            <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <span className="text-sm font-medium text-slate-700">{shortenNos(r.message)}</span>
          </Link>
        ))}
      </div>
    </Card>
  );
}


function AdminInviteReminder() {
  const { district_admin_account: daOn, campus_admin_account: caOn } = useFeatureFlags();
  const { viewOnly } = useAuth();
  const [oversight, setOversight] = useState(null);
  useEffect(() => { if (!viewOnly) api.get("/coach/oversight").then(({ data }) => setOversight(data)).catch(() => {}); }, [viewOnly]);
  // Suppress entirely when both admin account features are off, or in a read-only admin view.
  if (viewOnly || (!daOn && !caOn)) return null;
  if (!oversight) return null;
  const noAdmin = (oversight.district_admins || []).length === 0 && (oversight.campus_admins || []).length === 0;
  if (!noAdmin) return null;
  const which = daOn && caOn ? "District or Campus Administrator" : daOn ? "District Administrator" : "Campus Administrator";
  return (
    <Card className="mb-4 flex flex-wrap items-center gap-3 border-amber-300 bg-amber-50 p-4 shadow-sm animate-fade-in-up" data-testid="admin-invite-reminder-banner">
      <ShieldAlert className="h-5 w-5 shrink-0 text-amber-600" />
      <span className="text-sm font-semibold text-slate-800">
        Your team has no {which}. Invite one so deletion requests and oversight are covered.
      </span>
      <Link to="/coach/profile?tab=settings&section=oversight" className="ml-auto" data-testid="admin-invite-reminder-link">
        <Button size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800">Invite an administrator</Button>
      </Link>
    </Card>
  );
}


const PLAN_BADGE = {
  base: { label: "Base plan", cls: "bg-slate-100 text-slate-600" },
  standard: { label: "Standard plan", cls: "bg-emerald-100 text-emerald-700" },
  full: { label: "Full plan", cls: "bg-yellow-100 text-yellow-700" },
  theatre: { label: "Theatre plan", cls: "bg-rose-100 text-rose-700" },
  custom: { label: "Custom plan", cls: "bg-blue-100 text-blue-700" },
};

function PlanBadge({ plan }) {
  const meta = PLAN_BADGE[plan];
  if (!meta) return null;
  return <Badge className={`border-0 ${meta.cls}`} data-testid="coach-plan-badge">{meta.label}</Badge>;
}



export default function CoachDashboard() {
  const { user } = useAuth();
  const { choices: choicesOn, schedule: scheduleOn, rally_books: rallyBooksOn, document_depot: docDepotOn, attendance: attendanceOn, conflicts: conflictsOn, volunteers: volunteersOn, thespian: thespianOn, show_notes: showNotesOn, plan, merit_tracking: meritOn, lettering: letteringOn } = useFeatureFlags();
  const [d, setD] = useState(null);
  const [choices, setChoices] = useState([]);
  const [mbSummary, setMbSummary] = useState(null);
  const [pendingPayments, setPendingPayments] = useState(0);
  const [thespianPending, setThespianPending] = useState(0);
  const navigate = useNavigate();
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNowMs(Date.now()), 30000); return () => clearInterval(t); }, []);
  useEffect(() => { api.get("/coach/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/coach/choices").then(({ data }) => setChoices(data.items || [])).catch(() => {}); }, []);
  useEffect(() => { if (rallyBooksOn) cachedGet("/ledger/main-book-summary", setMbSummary).catch(() => {}); }, [rallyBooksOn]);
  useEffect(() => { if (rallyBooksOn) cachedGet("/ledger/payments?status=pending", (d) => setPendingPayments((d.items || []).length)).catch(() => {}); }, [rallyBooksOn]);
  useEffect(() => { if (thespianOn) api.get("/thespian/pending-count").then(({ data }) => setThespianPending(data.count || 0)).catch(() => {}); }, [thespianOn]);
  const showNotesPending = useShowNotesPending(showNotesOn);
  const showLettering = letteringOn && (d?.pending_lettering || 0) > 0;
  const showThespian = thespianOn && thespianPending > 0;
  const showShowNotes = showNotesOn && showNotesPending > 0;
  const meritNeedsAttention = ["pending_merit_requests", "cases_awaiting_ack", "requested_conferences", "upcoming_conferences", "past_open_conferences"]
    .some((k) => (d?.[k] || 0) > 0);
  const topCols = { 3: "lg:grid-cols-3", 4: "lg:grid-cols-4" }[1 + [showLettering, showThespian, showShowNotes].filter(Boolean).length] || "";

  // Open choices, soonest-expiring first (deadlined before undated), top 3.
  const topChoices = choices
    .filter((c) => c.accepting)
    .sort((a, b) => {
      if (a.deadline && b.deadline) return new Date(a.deadline) - new Date(b.deadline);
      if (a.deadline) return -1;
      if (b.deadline) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    })
    .slice(0, 3);

  const money = (n) => { const v = Number(n || 0); return `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(2)}`; };
  const mainNet = mbSummary?.combined_net || 0;

  return (
    <div>
      <PageHeader title={`Welcome, Coach ${user.last_name}`} subtitle={d ? `${[d.current_campus, d.current_team || "Team"].filter(Boolean).join(" ")} · Active year ${schoolYear(d.active_year)}` : "Loading…"}
        actions={<div className="flex items-center gap-2"><PlanBadge plan={plan} /><BroadcastDialog variant="coach" /></div>} />
      <PendingRemindersBanner />
      <AdminInviteReminder />
      <TheatreSetupCard />
      <CoachDeletionRequests />
      <CoachStaffRequests />
      {attendanceOn && <AttendanceTodayCard testId="coach-attendance-card" />}
      <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${topCols}`}>
        <SummaryCard testId="coach-stat-members" label="Team Members" value={d?.team_members ?? "—"} icon={Users} accent="sky" delay={0} to="/coach/members" hint={d ? `${d.pending_requests} pending` : ""} />
        {showLettering && <SummaryCard testId="coach-stat-lettering" label="Lettering Awards" value={d.pending_lettering} icon={Shield} accent="lettering" delay={60} to="/coach/lettering" hint="Requests awaiting review" />}
        {showThespian && <SummaryCard testId="coach-stat-thespian" label="Thespian Points" value={thespianPending} icon={Drama} accent="thespian" delay={120} to="/coach/thespian" hint="Claims awaiting review" />}
        {showShowNotes && <SummaryCard testId="coach-stat-show-notes" label="Show Notes" value={showNotesPending} icon={Clapperboard} accent="shownotes" delay={180} to="/coach/show-notes?tab=review"
          hint={`${showNotesPending === 1 ? "Report" : "Reports"} awaiting approval`} />}
      </div>

      {meritOn && meritNeedsAttention && <Link to="/coach/merits" className="mt-4 block">
        <Card data-testid="coach-stat-merits" className="p-5 border-slate-200 shadow-sm rounded-xl bg-white hover:shadow-md transition-transform hover:-translate-y-0.5 cursor-pointer animate-fade-in-up">
          <div className="mb-4 flex items-center gap-2">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Merit System</p>
            <ChevronRight className="h-4 w-4 text-slate-400" />
          </div>
          <div className="grid grid-cols-2 divide-slate-100 sm:grid-cols-4 sm:divide-x">
            {[
              { testId: "merit-stat-requests", label: "Requests awaiting review", value: d?.pending_merit_requests, icon: MessageSquareWarning, accent: "bg-amber-50 text-amber-600" },
              { testId: "merit-stat-ack", label: "Cases awaiting acknowledgement", value: d?.cases_awaiting_ack, icon: Award, accent: "bg-emerald-50 text-emerald-600" },
              { testId: "merit-stat-requested-conferences", label: "Requested conferences", value: d?.requested_conferences, icon: Gavel, accent: "bg-rose-50 text-rose-600" },
              { testId: "merit-stat-conferences", label: "Upcoming conferences", value: d?.upcoming_conferences, icon: CalendarClock, accent: "bg-blue-50 text-blue-600",
                note: d?.past_open_conferences ? `+${d.past_open_conferences} past, not yet closed` : null },
            ].map((s) => (
              <div key={s.testId} data-testid={s.testId} className="flex flex-col items-center px-3 text-center">
                <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${s.accent}`}><s.icon className="h-5 w-5" /></span>
                <p className="mt-3 text-3xl font-extrabold text-slate-900 font-heading tabular-nums">{s.value ?? "—"}</p>
                <p className="mt-1 text-xs font-semibold text-slate-500">{s.label}</p>
                {s.note && <p className="mt-0.5 text-[11px] font-semibold text-amber-700" data-testid={`${s.testId}-note`}>{s.note}</p>}
              </div>
            ))}
          </div>
        </Card>
      </Link>}

      {conflictsOn && <CoachConflictsCard />}
      {volunteersOn && <CoachGivenUpCard />}
      {volunteersOn && <CoachVolunteerCard />}

      {rallyBooksOn && (
      <Link to="/coach/ledger" className="mt-4 block">
        <Card data-testid="coach-ledger-card" className="p-5 border-slate-200 shadow-sm rounded-xl bg-white hover:shadow-md transition-transform hover:-translate-y-0.5 cursor-pointer animate-fade-in-up">
          <div className="mb-4 flex items-center gap-2">
            <Library className="h-4 w-4 text-slate-500" />
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Rally Books</p>
            <ChevronRight className="ml-auto h-4 w-4 text-slate-400" />
          </div>
          <div className="flex flex-col items-center px-3 text-center" data-testid="coach-ledger-net">
            <span className={`relative flex h-11 w-11 items-center justify-center rounded-xl ${mainNet >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>
              <DollarSign className="h-5 w-5" />
              {pendingPayments > 0 && (
                <button type="button" data-testid="coach-ledger-pending-alert"
                  title={`${pendingPayments} pending payment approval${pendingPayments === 1 ? "" : "s"} — review in Team Member Book`}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/coach/ledger?tab=member"); }}
                  className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-indigo-100 px-1 text-[10px] font-bold tabular-nums text-blue-700 shadow ring-2 ring-white transition-colors hover:bg-indigo-200">
                  {pendingPayments}
                </button>
              )}
            </span>
            <p className={`mt-3 text-3xl font-extrabold font-heading tabular-nums ${mainNet >= 0 ? "text-emerald-600" : "text-red-600"}`}>{mbSummary ? money(mainNet) : "—"}</p>
            <p className="mt-1 text-xs font-semibold text-slate-500">Main Book net{mbSummary ? (mainNet >= 0 ? " (surplus)" : " (deficit)") : ""}</p>
          </div>
        </Card>
      </Link>
      )}

      {docDepotOn && (d?.pending_documents ?? 0) > 0 && (
      <Link to="/coach/document-review" className="mt-4 block">
        <Card data-testid="coach-document-card" className="p-5 border-slate-200 shadow-sm rounded-xl bg-white hover:shadow-md transition-transform hover:-translate-y-0.5 cursor-pointer animate-fade-in-up">
          <div className="mb-4 flex items-center gap-2">
            <FolderArchive className="h-4 w-4 text-slate-500" />
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Document Depot</p>
            <ChevronRight className="ml-auto h-4 w-4 text-slate-400" />
          </div>
          <div className="flex flex-col items-center px-3 text-center" data-testid="coach-document-pending">
            <span className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
              <FolderArchive className="h-5 w-5" />
              {(d?.pending_documents ?? 0) > 0 && (
                <span data-testid="coach-document-pending-badge"
                  className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-orange-100 px-1 text-[10px] font-bold tabular-nums text-orange-700 shadow ring-2 ring-white">
                  {d.pending_documents}
                </span>
              )}
            </span>
            <p className="mt-3 text-3xl font-extrabold text-slate-900 font-heading tabular-nums">{d?.pending_documents ?? "—"}</p>
            <p className="mt-1 text-xs font-semibold text-slate-500">Uploads awaiting review</p>
          </div>
        </Card>
      </Link>
      )}

      {choicesOn && topChoices.length > 0 && (
        <Link to="/coach/choices" className="mt-4 block">
          <Card data-testid="coach-choices-card" className="p-5 border-slate-200 shadow-sm rounded-xl bg-white hover:shadow-md transition-transform hover:-translate-y-0.5 cursor-pointer animate-fade-in-up">
            <div className="mb-4 flex items-center gap-2">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Choices · Open Prompts</p>
              <span className="ml-auto flex h-11 w-11 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                <CheckCircle2 className="h-5 w-5" />
              </span>
              <ChevronRight className="h-4 w-4 text-slate-400" />
            </div>
            <div className="divide-y divide-slate-100">
              {topChoices.map((c) => {
                const allIn = c.targeted_count > 0 && c.response_count >= c.targeted_count;
                const cd = !allIn && c.accepting ? countdown(c.deadline, nowMs) : null;
                return (
                <div key={c.id} data-testid={`coach-choice-summary-${c.id}`} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-800">{c.title}</p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                      <p className="text-xs text-slate-400">{c.deadline ? `Closes ${fmtDeadline(c.deadline)}` : "No deadline"}</p>
                      {allIn ? (
                        <span data-testid={`coach-choice-allin-${c.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" /> 100% received
                        </span>
                      ) : cd && (
                        <span data-testid={`coach-choice-countdown-${c.id}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${cd.urgent ? "bg-red-100 text-red-700 animate-pulse" : "bg-amber-100 text-amber-700"}`}>
                          <Clock className="h-3 w-3" /> {cd.label}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-2xl font-extrabold tabular-nums text-slate-900 font-heading" data-testid={`coach-choice-count-${c.id}`}>{c.response_count}</p>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">response{c.response_count === 1 ? "" : "s"}</p>
                  </div>
                </div>
                );
              })}
            </div>
          </Card>
        </Link>
      )}

      {scheduleOn && <UpcomingEvents endpoint="/coach/schedule" to="/coach/schedule" />}
      <DashboardNotificationsCard />
    </div>
  );
}
